import { Cpu, CheckCircle2, XCircle, Clock, FileText, Wifi, WifiOff } from 'lucide-react';
import { useJobStream } from '@/hooks/useJobStream';

const statusIcons = {
  queued: Clock,
  classified: FileText,
  extracting: Cpu,
  done: CheckCircle2,
  skipped: CheckCircle2,
  error: XCircle,
};

const statusColors = {
  queued: 'text-muted-foreground',
  classified: 'text-blue-400',
  extracting: 'text-primary',
  done: 'text-emerald-400',
  skipped: 'text-muted-foreground',
  error: 'text-rose-400',
};

const barColors = {
  queued: 'bg-slate-700',
  classified: 'bg-blue-600',
  extracting: 'bg-orange-500',
  done: 'bg-emerald-500',
  skipped: 'bg-slate-600',
  error: 'bg-rose-500',
};

export default function ProcessingProgress({ jobId, onComplete }) {
  const { progress, connected, usingSSE } = useJobStream(jobId, {
    enabled: true,
    onComplete,
  });

  if (!progress) {
    return (
      <div className="flex items-center gap-2 p-4">
        <Cpu className="w-4 h-4 text-primary animate-pulse" />
        <span className="text-xs font-mono text-muted-foreground">Connecting to pipeline...</span>
      </div>
    );
  }

  const pctDone = progress.percent_complete || 0;
  const stageLbl = {
    queued: 'Queued',
    classifying: 'Classifying Pages',
    extracting: 'Extracting Data',
    extraction_complete: 'Complete',
    error: 'Error',
  }[progress.pipeline_stage] || progress.pipeline_stage;

  return (
    <div className="bg-card border border-border rounded-sm" data-testid="processing-progress">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Cpu className={`w-4 h-4 ${progress.status === 'processing' ? 'text-primary animate-pulse' : 'text-emerald-400'}`} />
          <span className="font-heading font-bold text-xs uppercase tracking-wider text-foreground">
            Pipeline: {stageLbl}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/* Connection indicator */}
          <div className="flex items-center gap-1" data-testid="sse-status">
            {connected ? (
              <Wifi className={`w-3 h-3 ${usingSSE ? 'text-emerald-400' : 'text-amber-400'}`} />
            ) : (
              <WifiOff className="w-3 h-3 text-muted-foreground" />
            )}
            <span className="font-mono text-[9px] text-muted-foreground">
              {usingSSE ? 'LIVE' : connected ? 'POLL' : '...'}
            </span>
          </div>
          <span className="font-mono text-xs text-muted-foreground">
            {progress.pages_done}/{progress.total_pages} pages
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-4 py-2">
        <div className="h-2 bg-secondary rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-orange-500 to-emerald-500 rounded-full transition-all duration-300"
            style={{ width: `${pctDone}%` }}
          />
        </div>
        <div className="flex justify-between mt-1">
          <span className="font-mono text-[10px] text-muted-foreground">{pctDone.toFixed(0)}% complete</span>
          {progress.pages_errored > 0 && (
            <span className="font-mono text-[10px] text-rose-500">{progress.pages_errored} error(s)</span>
          )}
        </div>
      </div>

      {/* Page list */}
      <div className="max-h-[300px] overflow-auto divide-y divide-slate-800/30">
        {(progress.pages || []).map((page) => {
          const Icon = statusIcons[page.status] || Clock;
          return (
            <div key={page.page_index} className="flex items-center gap-3 px-4 py-1.5 hover:bg-secondary/20 transition-colors" data-testid={`progress-page-${page.page_index}`}>
              <Icon className={`w-3.5 h-3.5 shrink-0 ${statusColors[page.status] || 'text-muted-foreground'} ${page.status === 'extracting' ? 'animate-pulse' : ''}`} />
              <span className="font-mono text-[10px] text-muted-foreground w-10 shrink-0">P{page.page_index + 1}</span>
              {page.page_type && (
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wide border ${
                  page.page_type === 'floor_plan' ? 'bg-blue-950/50 text-blue-400 border-blue-900' :
                  page.page_type === 'room_schedule' ? 'bg-emerald-950/50 text-emerald-400 border-emerald-900' :
                  page.page_type === 'equipment_schedule' ? 'bg-violet-950/50 text-violet-400 border-violet-900' :
                  page.page_type === 'mep_plan' ? 'bg-orange-950/50 text-primary border-orange-900' :
                  'bg-secondary/50 text-muted-foreground border-border'
                }`}>
                  {page.page_type.replace(/_/g, ' ')}
                </span>
              )}
              <span className="font-mono text-[10px] text-muted-foreground truncate flex-1">{page.detail}</span>
              <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${barColors[page.status] || 'bg-slate-600'}`} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
